import { reactive } from 'vue';
import api from './ApiClient.js';

// Centralized reactive store
export const store = reactive({
  movies: [],
  currentMovie: null,
  jobs: new Map(),
  loading: false,
  error: '',
  success: ''
});

export const storeActions = {
  async loadMovies() {
    store.loading = true;
    store.error = '';
    try {
      store.movies = await api.listMovies();
    } catch (e) {
      store.error = e.message;
    } finally {
      store.loading = false;
    }
  },
  async loadMovie(id) {
    store.loading = true;
    store.error = '';
    try {
      store.currentMovie = await api.getMovie(id);
    } catch (e) {
      store.error = e.message;
    } finally {
      store.loading = false;
    }
  },
  async createMovie(data) {
    store.loading = true;
    store.error = '';
    try {
      const result = await api.createMovie(data);
      if (result.movie && result.jobId) {
        store.currentMovie = result.movie;
        store.jobs.set(result.jobId, {
          id: result.jobId,
          status: 'pending',
          progress: 0,
          createdAt: Date.now()
        });
        if (!store.movies.find((m) => m.id === result.movie.id)) {
          store.movies.push(result.movie);
        }
        store.success = 'Movie created successfully. Job started for enrichment.';
      } else {
        store.currentMovie = result;
        store.movies.push(result);
        store.success = 'Movie created successfully.';
      }
      return result;
    } catch (e) {
      store.error = e.message;
    } finally {
      store.loading = false;
    }
  },
  async updateMovie(id, data) {
    store.loading = true;
    store.error = '';
    try {
      const result = await api.updateMovie(id, data);
      if (result.movie) {
        store.currentMovie = result.movie;
        const idx = store.movies.findIndex((m) => m.id === result.movie.id);
        if (idx !== -1) {
          store.movies[idx] = result.movie;
        }
        store.success = 'Movie updated successfully.';
      } else {
        store.currentMovie = result;
        store.success = 'Movie updated successfully.';
      }
    } catch (e) {
      store.error = e.message;
    } finally {
      store.loading = false;
    }
  },
  async deleteMovie(id) {
    store.loading = true;
    store.error = '';
    try {
      await api.deleteMovie(id);
      if (store.currentMovie && store.currentMovie.id === id) {
        store.currentMovie = null;
      }
      store.movies = store.movies.filter((m) => m.id !== id);
      store.success = 'Movie deleted successfully.';
    } catch (e) {
      store.error = e.message;
    } finally {
      store.loading = false;
    }
  },
  async checkJobStatus(jobId) {
    try {
      const job = await api.getJobStatus(jobId);
      store.jobs.set(jobId, job);
    } catch (e) {
      store.jobs.delete(jobId);
    }
  },
  clearJob(jobId) {
    store.jobs.delete(jobId);
  },
  setCurrentMovie(movie) {
    store.currentMovie = movie;
  },
  clearCurrentMovie() {
    store.currentMovie = null;
  },
  clearMessages() {
    store.error = '';
    store.success = '';
  },
  setLoading(loading) {
    store.loading = loading;
  }
};

export function useStore() {
  return {
    store,
    ...storeActions
  };
}
